<template>
	<div>
		<!-- heading row -->
		<v-row class="mx-2 mt-1">
			<v-col cols="12" md="6">
				<h2 class="text-left">Transactions Report</h2>
			</v-col>
			<v-spacer></v-spacer>
		</v-row>

		<v-row>
			<v-col cols="12">
				<v-card class="mx-2">
					<v-tabs class="px-4" v-model="tab">
						<v-tab>Analytics</v-tab>
						<v-tab>Category Summary</v-tab>
						<v-tab>Account Summary</v-tab>
						<v-tab>Compare</v-tab>

						<!-- ANALYTICS TAB -->
						<v-tab-item>
							<v-row class="my-1" justify="center">
								<v-col cols="12" md="4" sm="5" align-self="center">
									<!-- start date picker -->
									<v-menu
										v-model="startMenu"
										:close-on-content-click="false"
										:nudge-right="40"
										transition="scale-transition"
										offset-y
										min-width="auto"
										>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="formattedDate(startDate)"
												label="Start Date"
												readonly
												v-bind="attrs"
												v-on="on"
												outlined
												dense
												class="rounded-input"
												hide-details
											></v-text-field>
										</template>
										<v-date-picker
											v-model="startDate"
											@input="startMenu = false"
										></v-date-picker>
									</v-menu>
								</v-col>

								<v-col cols="12" md="4" sm="5" align-self="center">
									<!-- end date picker -->
									<v-menu
										v-model="endMenu"
										:close-on-content-click="false"
										:nudge-right="40"
										transition="scale-transition"
										offset-y
										min-width="auto"
										class="date-picker-menu"
										>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="formattedDate(endDate)"
												label="End Date"
												readonly
												v-bind="attrs"
												v-on="on"
												outlined
												dense
												class="rounded-input"
												hide-details
											></v-text-field>
										</template>
										<v-date-picker
											v-model="endDate"
											@input="endMenu = false"
                      
										></v-date-picker>
									</v-menu>
								</v-col>

								<v-col cols="12" md="1" sm="2">
									<v-btn
										block
										color="primary"
										@click="getAnalyticsData()"
									>
										<v-icon>
											mdi-sync
										</v-icon>
									</v-btn>
								</v-col>
							</v-row>

							<v-row class="pa-2 my-1" justify="center">
								<v-col cols="12" md="4" sm="6" align-self="center">
									<h2 class="mb-2">
										Total Income
									</h2>
									<h2 class="green--text">
										{{ totals.total_income!=null ? currencyFormat(totals.total_income) : '-' }}
									</h2>
								</v-col>
								<v-col cols="12" md="4" sm="6" align-self="center">
									<h2 class="mb-2">
										Total Expenses
									</h2>
									<h2 class="orange--text">
										{{ totals.total_expense!=null ? currencyFormat(totals.total_expense) : '-' }}
									</h2>
								</v-col>
								<v-col cols="12" md="4" sm="6" align-self="center">
									<div v-if="totals.total_expense > totals.total_income">
										<h2 class="mb-2">
											Total Loss
										</h2>
										<h2 class="red--text">
											{{ ((totals.total_income!=null && totals.total_expense!=null) && totals.total_expense >= totals.total_income )
													?
												currencyFormat(totals.total_expense - totals.total_income) : currencyFormat(0)
											}}
										</h2>
									</div>
									<div v-else>
										<h2 class="mb-2">
											Total Profit
										</h2>
										<h2 class="green--text">
											{{ ((totals.total_income!=null && totals.total_expense!=null) && totals.total_income >= totals.total_expense )
													?
												currencyFormat(totals.total_income - totals.total_expense) : '-' 
											}}
										</h2>
									</div>
								</v-col>
							</v-row>

							<v-row>
								<v-col>
									<div v-if="startDate && endDate && transactionAnalyticsValues && transactionAnalyticsLabels">
										<TransactionColumnChart
											:colors="['rgb(0, 227, 150)', '#F44336']"
											:series="transactionAnalyticsValues"
											:categories="transactionAnalyticsLabels"
										/>
									</div>
								</v-col>
							</v-row>

							<v-row class="my-1">
								<v-col cols="12" md="6">
									<div class="d-flex justify-center" v-if="startDate && endDate && incomePieValues && incomePieLabels">
										<StaffAttendanceAnalytics
											:series="incomePieValues"
											:labels="incomePieLabels"
											position="bottom"
											:width="300"
											:show="incomePieValues.length ? true : false"
										/>
									</div>
								</v-col>
								<v-col cols="12" md="6">
									<div class="d-flex justify-center" v-if="startDate && endDate && expensePieValues && expensePieLabels">
										<StaffAttendanceAnalytics
											:series="expensePieValues"
											:labels="expensePieLabels"
											position="bottom"
											:width="300"
											:show="expensePieValues.length ? true : false"
										/>
									</div>
								</v-col>
							</v-row>

							<v-row class="my-1">
								<v-col cols="12">
									<div v-if="startDate && endDate && modeDataAnalytics && modeDataAnalytics.length">
										<TransactionColumnChart
											:colors="['rgb(0, 227, 150)', '#F44336']"
											:series="modeDataAnalytics"
											:categories="modeDataLabels"
										/> 
									</div>
								</v-col>
							</v-row>
						</v-tab-item>

						<!-- CATEGORY SUMMARY TAB -->
						<v-tab-item>
							<CategorySummary />
						</v-tab-item>

						<v-tab-item>
							<AccountSummary />
						</v-tab-item>

						<!-- COMPARE TRANSACTIONS TAB -->
						<v-tab-item>
							<Compare />
						</v-tab-item>
					</v-tabs>
				</v-card>
			</v-col>
		</v-row>

		<!-- snackbar -->
		<v-snackbar v-model="snackbar.visible" :color="snackbar.color" multi-line>
			{{ snackbar.message }}
			<template v-slot:action="{ attrs }">
				<v-btn dark text v-bind="attrs" @click="snackbar.visible = false"
					>Close</v-btn
				>
			</template>
		</v-snackbar>

	</div>
</template>

<script>
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import api from "@api/index";
import endpoints from "@api/repository";
import Helper from "@utils/misc.js";
import TransactionColumnChart from "@components/finance/TransactionColumnChart.vue";
import StaffAttendanceAnalytics from "@components/staff_attendance/StaffAttendanceAnalytics.vue"
import Compare from "@components/finance/Compare";
import CategorySummary from "@components/finance/CategorySummary";
import AccountSummary from "@components/finance/AccountSummary";
import TourHelper from "@utils/tour_helper";



export default {
	name: "TransactionsReport",

	components: {
		CategorySummary,
		AccountSummary,
		Compare,
		TransactionColumnChart,
		StaffAttendanceAnalytics,
	},

	data() {
		return {
			tab: 0,
			snackbar: {
				visible: false,
				message: "",
				color: "dark",
			},

			totals: {
				total_income: 0,
				total_expense: 0,
			},

			moment: moment,

			menu: false,
			startDate: null,
			endDate: null,
			startMenu: false,
			endMenu: false,

			// TRANSACTION ANALYTICS (INCOME/EXPENSE DOOUBLE-BAR GRAPH)
			transactionAnalyticsValues: null,
			transactionAnalyticsLabels: null,

			// INCOME PIE CHART
			incomePieLabels: null,
			incomePieValues: null,

			// EXPENSE PIE CHART
			expensePieLabels: null,
			expensePieValues: null,

			// PAYMENT MODES DOUBLE-BAR GRAPH
			modeDataLabels: null,
			modeDataAnalytics: null,
		};
	},

	computed: {
		...mapGetters(["currentUser", "accessToken", "isLoading"]),

		apiEssentials() {
			return {
				accessToken: this.accessToken,
				setLoading: this.setLoading,
				handleErrorsFunction: this.handleApiErrors,
			};
		},
	},

	methods: {
		...mapActions(["setLoading"]),

		handleTour() {
        var steps = [];
        steps.push({id: '1', title: "Welcome to Transaction Report", description: "View your Transaction report represented in graphs and statistics.", videoLink: "https://youtube.com"});
        // steps.push({id: '2', description: "Add your income by filling neccessary information", el: '#add-income'});
        // steps.push({id: '3', description: "If amount is <span style='color:green;'>pending</span> then it can be authorized in authorize income", position:'top' ,el: '#income-table'});
        // steps.push({id: '4', description: "Take a quick look at payslips which require for further action, click on generate to generate payslip.", el: '#pending-actions-dashboard'});
        // steps.push({id: '5', description: "Check all your payslips which are pending for approval.", el: '#approval-dashboard'});
        TourHelper.startTour(this, "transaction__reports", steps);
    },

		// handle api errors
		async handleApiErrors(err) {
			this.setLoading(false);
			console.log(err);
			if (err.response) {
				this.snackbar.text = "Unable to process request";
				this.snackbar.color = "error";
				this.snackbar.visible = true;
			}
		},

		currencyFormat: Helper.currencyFormat,

		formattedDate(date) {
			if (!date) return;

			var formatted = moment(date).format("ll")
			return formatted;
		},

		// date field validations
		validate() {
			if (!this.startDate) {		// if start date is empty
				this.snackbar.message = "Please select Start Date";
				this.snackbar.visible = true;
				this.snackbar.color = "error";
				return false;
			} 
			else if (!this.endDate) {	// if end date is empty
				this.snackbar.message = "Please select End Date";
				this.snackbar.visible = true;
				this.snackbar.color = "error";
				return false;
			}
			else if(moment(this.endDate, 'YYYY-MM-DD').isBefore(moment(this.startDate), 'YYYY-MM-DD'))    // check if endDate is not before startDate
			{
				this.snackbar.message = "End Date cannot be before Start Date! Please select proper dates"
				this.snackbar.visible = true
				this.snackbar.color = "error"
				return false;
			}

			return true;
		},

		// fetch analytics data via api call
		async getAnalyticsData() {
			var url = endpoints.financeAnalytics;
			
			// proceed only if both dates are validated
			if(this.validate()) {

				url = Helper.addUrlParams(url, [
					`transaction_date__gte=${this.startDate}`,
					`transaction_date__lte=${this.endDate}`,
				]);

				var response = await api.call(this.apiEssentials, url)
				if (response) {
					this.totals = {}
					this.totals = response.totals

					//income-expense category bar graph
					this.transactionAnalyticsValues = []
					this.transactionAnalyticsLabels = []

					let barDataIncomes = response.income_expense_analytics_data.map((data) => data.income || 0)		// get income values
					let barDataExpenses = response.income_expense_analytics_data.map((data) => data.expense || 0)		// get expense values

					this.transactionAnalyticsLabels = response.income_expense_analytics_data.map((data) => 		// form month,year labels
						`${moment(data.month,"MM").format('MMM')} ${data.year}`
					)

					// checks if all values are 0, if yes, sends empty list
					if(barDataIncomes.find(x => x != 0) == null) barDataIncomes = [];
					if(barDataExpenses.find(x => x != 0) == null) barDataExpenses = [];

					this.transactionAnalyticsValues.push({
						name: 'Incomes',
						data: barDataIncomes
					})
					this.transactionAnalyticsValues.push({
						name: 'Expenses',
						data: barDataExpenses
					})

					//income pie chart
					this.incomePieLabels = []
					this.incomePieValues = []	
			
					this.incomePieLabels = response.category_analytics_data.income.map((data) => data.title + " - "+ "<b>"+String(data.total_sum)+"</b>")
					this.incomePieValues = response.category_analytics_data.income.map((data) => data.total_sum)

					if(!this.incomePieValues.length) {	// if incomes from api response are 0 i.e. empty
						this.incomePieValues = []
					}

					//expense pie chart
					this.expensePieLabels = []
					this.expensePieValues = []	
			
					this.expensePieLabels = response.category_analytics_data.expense.map((data) => data.title + " - " + "<b>"+String(data.total_sum)+"</b>")
					this.expensePieValues = response.category_analytics_data.expense.map((data) => data.total_sum)

					if(!this.expensePieValues.length) {	// if expenses from api response are 0 i.e. empty
						this.expensePieValues = []																																															
					}

					// Mode Income/Expense Bar values and labels
					var modeDataIncomes = []
					var modeDataExpenses = []
					var trxModeLabels = new Set()

					// for income data
					response.transaction_mode_analytics_data[0].data.map((mode_data) => {
						modeDataIncomes[mode_data.transaction_mode__type] = mode_data.total_sum;

						// get transaction modes 
						switch(mode_data.transaction_mode__type) {
							case 0: trxModeLabels.add('Cash'); break;
							case 1: trxModeLabels.add('Cheque'); break;
							case 2: trxModeLabels.add('DD'); break;
							case 3: trxModeLabels.add('NetBanking'); break;
							case 4: trxModeLabels.add('NEFT/RTGS'); break;
							case 5: trxModeLabels.add('Debit Card'); break;
							case 6: trxModeLabels.add('Credit Card'); break;
						}
					});

					// for expense data
					response.transaction_mode_analytics_data[1].data.map((mode_data) => {
						modeDataExpenses[mode_data.transaction_mode__type] = mode_data.total_sum;

						// get transaction modes 
						switch(mode_data.transaction_mode__type) {
							case 0: trxModeLabels.add('Cash'); break;
							case 1: trxModeLabels.add('Cheque'); break;
							case 2: trxModeLabels.add('DD'); break;
							case 3: trxModeLabels.add('NetBanking'); break;
							case 4: trxModeLabels.add('NEFT/RTGS'); break;
							case 5: trxModeLabels.add('Debit Card'); break;
							case 6: trxModeLabels.add('Credit Card'); break;
						}
					});

					// this.modeDataLabels = ['Cash', 'Cheque', 'DD', 'NetBanking', 'NEFT/RTGS', 'Debit Card', 'Credit Card']
					this.modeDataLabels = Array.from(trxModeLabels)
					this.modeDataAnalytics = []

					this.modeDataAnalytics.push({
						name: 'Incomes',
						data: modeDataIncomes
					})
					this.modeDataAnalytics.push({
						name: 'Expenses',
						data: modeDataExpenses
					})
				}
			}
		},
	},

	watch: {
		$route() {
			// react to route changes...
			if (Object.prototype.hasOwnProperty.call(this.$route.query, "step")) {
				this.tab = Number(this.$route.query.step) - 1;
			}
		},
	},

	created() {
		if (Object.prototype.hasOwnProperty.call(this.$route.query, "step")) {
			this.tab = Number(this.$route.query.step) - 1;
		}

		let dates = Helper.getCurrentFiscalYearDates();
		if(dates.length == 2) {
			this.startDate = moment(dates[0]).format('YYYY-MM-DD')
			this.endDate = moment(dates[1]).format('YYYY-MM-DD')

			this.getAnalyticsData()
		}
		this.handleTour();
	},
};
</script>

<style scoped>
.date-picker-menu{
    z-index: 100 ;
}
</style>
